/**
 * Branding API service.
 *
 * Public (no-auth) endpoint for GET /api/v1/branding.
 * Auth-required endpoint for logo upload (backend-proxied, single POST).
 *
 * Public fetch uses dedicated fetch (not apiClient) because:
 * - The endpoint is unauthenticated (no Bearer token needed)
 * - Must work on login/public pages before auth hydration
 * - Follows the {data: {...}} envelope pattern
 *
 * Logo upload bypasses apiClient (which JSON-stringifies body) and uses
 * a direct fetch with the raw File as body + proper Content-Type.
 */

import type { BrandingPublicShape } from '@/config/brandingDefaults';

function getBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL || '/api';
}

function getAuthToken(): string | null {
  try {
    return localStorage.getItem('opus-ia-token');
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Public branding fetch (no auth)
// ---------------------------------------------------------------------------

/**
 * Fetch public branding configuration from the server.
 * No auth required -- works on login and public pages.
 *
 * @returns BrandingPublicShape or throws on network/parse error
 */
export async function getPublicBranding(): Promise<BrandingPublicShape> {
  const response = await fetch(`${getBaseUrl()}/v1/branding`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch branding: ${response.status}`);
  }

  const json = (await response.json()) as { data: BrandingPublicShape };
  return json.data;
}

// ---------------------------------------------------------------------------
// Logo upload (admin auth required) -- backend-proxied single POST
// ---------------------------------------------------------------------------

export interface LogoProcessResult {
  logo_url: string;
  logo_storage_key: string;
}

/**
 * Upload a logo image directly to the backend in a single request.
 *
 * The backend receives the raw image bytes, validates magic bytes,
 * strips EXIF, resizes, re-encodes as WebP, and stores in MinIO server-side.
 * Returns an API-served logo URL (browser-reachable, no internal MinIO exposure).
 *
 * @param file - The image File from the browser file input
 * @returns { logo_url, logo_storage_key }
 */
export async function uploadLogo(file: File): Promise<LogoProcessResult> {
  const token = getAuthToken();

  const response = await fetch(`${getBaseUrl()}/v1/organizations/current/logo`, {
    method: 'POST',
    headers: {
      'Content-Type': file.type,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: file,
  });

  if (!response.ok) {
    let errorMsg = `Logo upload failed: ${response.status}`;
    try {
      const body = await response.json() as { message?: string; error?: string };
      errorMsg = body.message ?? body.error ?? errorMsg;
    } catch {
      // response body is not JSON -- keep generic message
    }
    throw new Error(errorMsg);
  }

  const json = (await response.json()) as { data: LogoProcessResult };
  return json.data;
}
